import {
  PORTAL_EXTENSION_PROTOCOL_VERSION,
  PortalExtensionBridgeResponse,
  type PortalExtensionAccountBindStartPayload,
  type PortalExtensionAuthUser,
  type PortalExtensionBridgeErrorCode,
  type PortalExtensionBridgeRequest,
  type PortalExtensionStatusPayload,
  type AccountSyncRun,
} from '@ignite/types';

const BRIDGE_TIMEOUT_MS = 2_500;

export type PortalExtensionConnection =
  | 'connected'
  | 'disconnected'
  | 'expired'
  | 'bridge_unavailable'
  | 'background_unavailable'
  | 'account_mismatch'
  | 'protocol_error';

export type PortalExtensionStatus = {
  installed: boolean;
  connection: PortalExtensionConnection;
  extensionVersion: string | null;
  buildTarget: PortalExtensionStatusPayload['build_target'] | null;
  authUser: PortalExtensionAuthUser | null;
  tenantId: string | null;
  tokenExpiresAt: string | null;
  pendingCount: number;
  errorMessage: string | null;
};

export type AccountSyncExtensionReadinessFailure =
  | 'extension_missing'
  | 'extension_unavailable'
  | 'extension_disconnected'
  | 'extension_account_mismatch'
  | 'extension_environment_mismatch';

export class PortalExtensionBridgeError extends Error {
  constructor(
    readonly code: PortalExtensionBridgeErrorCode | 'bridge.unavailable',
    message: string,
  ) {
    super(message);
    this.name = 'PortalExtensionBridgeError';
  }
}

export function wenanExtensionStoreUrl(): string {
  return (
    import.meta.env.VITE_CHROME_WEB_STORE_URL?.trim() ||
    'https://chrome.google.com/webstore/category/extensions'
  );
}

export function extensionConnectUrl(): string {
  return '/auth/extension-connect';
}

export async function readWenanExtensionStatus(
  currentSession?: { userId: string; tenantId: string } | null,
): Promise<PortalExtensionStatus> {
  try {
    const response = await sendPortalExtensionRequest({
      ...portalRequestBase(),
      type: 'IGNITE_PORTAL_STATUS_REQUEST',
    });
    if (response.type !== 'IGNITE_PORTAL_STATUS_RESPONSE') {
      throw new PortalExtensionBridgeError('bridge.protocol_unsupported', 'unexpected response');
    }
    return statusFromPayload(response.payload, currentSession);
  } catch (error) {
    return statusFromBridgeError(error);
  }
}

export async function connectWenanExtension(
  code: string,
  currentSession?: { userId: string; tenantId: string } | null,
): Promise<PortalExtensionStatus> {
  const response = await sendPortalExtensionRequest({
    ...portalRequestBase(),
    type: 'IGNITE_PORTAL_CONNECT_REQUEST',
    payload: { code },
  });
  if (response.type !== 'IGNITE_PORTAL_CONNECT_RESPONSE') {
    throw new PortalExtensionBridgeError('bridge.protocol_unsupported', 'unexpected response');
  }
  return statusFromPayload(response.payload, currentSession);
}

export async function disconnectWenanExtension(): Promise<void> {
  const response = await sendPortalExtensionRequest({
    ...portalRequestBase(),
    type: 'IGNITE_PORTAL_DISCONNECT_REQUEST',
  });
  if (response.type !== 'IGNITE_PORTAL_DISCONNECT_RESPONSE') {
    throw new PortalExtensionBridgeError('bridge.protocol_unsupported', 'unexpected response');
  }
}

export async function startWenanAccountBinding(
  payload: PortalExtensionAccountBindStartPayload,
): Promise<{ tab: 'opened' | 'reused'; expiresAt: string }> {
  const response = await sendPortalExtensionRequest({
    ...portalRequestBase(),
    type: 'IGNITE_PORTAL_ACCOUNT_BIND_START_REQUEST',
    payload,
  });
  if (response.type !== 'IGNITE_PORTAL_ACCOUNT_BIND_START_RESPONSE') {
    throw new PortalExtensionBridgeError('bridge.protocol_unsupported', 'unexpected response');
  }
  return { tab: response.payload.tab, expiresAt: response.payload.expires_at };
}

export async function startWenanAccountSync(input: {
  accountId: string;
  externalId: string;
  bindingVersion: number;
  syncRun: AccountSyncRun;
}): Promise<void> {
  const response = await sendPortalExtensionRequest({
    ...portalRequestBase(),
    type: 'IGNITE_PORTAL_ACCOUNT_SYNC_START_REQUEST',
    payload: {
      account_id: input.accountId,
      external_id: input.externalId,
      binding_version: input.bindingVersion,
      sync_run: input.syncRun,
    },
  });
  if (response.type !== 'IGNITE_PORTAL_ACCOUNT_SYNC_START_RESPONSE') {
    throw new PortalExtensionBridgeError('bridge.protocol_unsupported', 'unexpected response');
  }
}

export function accountSyncExtensionReadiness(
  status: PortalExtensionStatus,
  portalOrigin: string,
): AccountSyncExtensionReadinessFailure | null {
  if (!status.installed) return 'extension_missing';
  if (
    status.connection === 'bridge_unavailable' ||
    status.connection === 'background_unavailable' ||
    status.connection === 'protocol_error'
  ) {
    return 'extension_unavailable';
  }
  if (status.connection === 'account_mismatch') return 'extension_account_mismatch';
  if (status.connection !== 'connected') return 'extension_disconnected';

  const expectedBuildTarget = expectedBuildTargetForPortalOrigin(portalOrigin);
  if (expectedBuildTarget && status.buildTarget !== expectedBuildTarget) {
    return 'extension_environment_mismatch';
  }
  return null;
}

export function sendPortalExtensionRequest(
  request: PortalExtensionBridgeRequest,
  timeoutMs = BRIDGE_TIMEOUT_MS,
): Promise<PortalExtensionBridgeResponse> {
  if (typeof window === 'undefined') {
    return Promise.reject(
      new PortalExtensionBridgeError('bridge.unavailable', 'window unavailable'),
    );
  }

  return new Promise((resolve, reject) => {
    const cleanup = () => {
      window.clearTimeout(timeout);
      window.removeEventListener('message', handleMessage);
    };
    const fail = (error: PortalExtensionBridgeError) => {
      cleanup();
      reject(error);
    };
    const handleMessage = (event: MessageEvent<unknown>) => {
      if (event.source !== window || event.origin !== window.location.origin) return;
      const envelope = event.data;
      if (!isMatchingEnvelope(envelope, request.request_id)) return;
      if (envelope.protocol_version !== PORTAL_EXTENSION_PROTOCOL_VERSION) {
        fail(new PortalExtensionBridgeError('bridge.protocol_unsupported', 'protocol mismatch'));
        return;
      }
      const parsed = PortalExtensionBridgeResponse.safeParse(envelope);
      if (!parsed.success) {
        fail(new PortalExtensionBridgeError('bridge.protocol_unsupported', 'invalid response'));
        return;
      }
      cleanup();
      if (!parsed.data.ok) {
        reject(
          new PortalExtensionBridgeError(
            parsed.data.error.code,
            parsed.data.error.message ?? parsed.data.error.code,
          ),
        );
        return;
      }
      resolve(parsed.data);
    };
    const timeout = window.setTimeout(
      () => fail(new PortalExtensionBridgeError('bridge.unavailable', 'bridge timeout')),
      timeoutMs,
    );
    window.addEventListener('message', handleMessage);
    window.postMessage(request, window.location.origin);
  });
}

function portalRequestBase() {
  return {
    protocol_version: PORTAL_EXTENSION_PROTOCOL_VERSION,
    source: 'ignite-portal' as const,
    target: 'ignite-extension' as const,
    request_id: crypto.randomUUID(),
  };
}

function isMatchingEnvelope(
  value: unknown,
  requestId: string,
): value is Record<string, unknown> & { protocol_version: unknown } {
  return (
    value !== null &&
    typeof value === 'object' &&
    'source' in value &&
    value.source === 'ignite-extension' &&
    'target' in value &&
    value.target === 'ignite-portal' &&
    'request_id' in value &&
    value.request_id === requestId &&
    'protocol_version' in value
  );
}

function statusFromPayload(
  payload: PortalExtensionStatusPayload,
  currentSession?: { userId: string; tenantId: string } | null,
): PortalExtensionStatus {
  const accountMismatch =
    Boolean(currentSession) &&
    Boolean(payload.auth_user?.id) &&
    (payload.auth_user?.id !== currentSession?.userId ||
      payload.tenant_id !== currentSession?.tenantId);
  return {
    installed: true,
    connection: accountMismatch ? 'account_mismatch' : payload.auth_state,
    extensionVersion: payload.extension_version,
    buildTarget: payload.build_target,
    authUser: payload.auth_user,
    tenantId: payload.tenant_id,
    tokenExpiresAt: payload.token_expires_at,
    pendingCount: payload.pending_count,
    errorMessage: null,
  };
}

function statusFromBridgeError(error: unknown): PortalExtensionStatus {
  const bridgeError =
    error instanceof PortalExtensionBridgeError
      ? error
      : new PortalExtensionBridgeError('internal_error', errorMessage(error));
  const connection: PortalExtensionConnection =
    bridgeError.code === 'bridge.unavailable'
      ? 'bridge_unavailable'
      : bridgeError.code === 'bridge.background_unavailable'
        ? 'background_unavailable'
        : 'protocol_error';
  return {
    installed: connection !== 'bridge_unavailable',
    connection,
    extensionVersion: null,
    buildTarget: null,
    authUser: null,
    tenantId: null,
    tokenExpiresAt: null,
    pendingCount: 0,
    errorMessage: bridgeError.message,
  };
}

function expectedBuildTargetForPortalOrigin(portalOrigin: string): 'production' | 'staging' | null {
  try {
    const hostname = new URL(portalOrigin).hostname;
    if (hostname === 'app.wenan.ws') return 'production';
    if (hostname === 'app-staging.zale.net') return 'staging';
  } catch {
    return null;
  }
  return null;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'extension bridge unavailable';
}
